import axios from 'axios'
import store from '../index'
import { make } from 'vuex-pathify'
import { showError, allow } from '@/util/helpers'
import { PERMISSIONS } from '@/enums/permissions'
import { getNomenclatura, NOMENCLATURA_PEDIDO } from '@/enums/nomenclaturas'

const state = {
  version: 1,

  initialized: false,
  drawer: null,
  drawerImage: true,
  mini: false,

  dialogActions: false,
  dialogFeedback: false,

  dialogPassword: false,
  dialogBlockByPayment: false,
  isPasswordValid: false,
  dialogErrorCompany: false,
  errorCompanyText: '',

  payment: {
    dialog: false,
  },

  loading: false,

  accessControl: null,

  items: [],
}

const mutations = make.mutations(state)

const actions = {
  init: async ({ dispatch, commit }, user) => {
    if (!user) {
      return false
    }

    // set user
    store.commit('authStore/setUser', user)

    // load user permissions
    const permissions = await store.dispatch('authStore/getUserPermissions', user.id)
    await store.dispatch('authStore/setUserConfig', user)

    const selectedCompanyId = Number(localStorage.getItem(`campag:selected-company:${user.id}`))
    let company = null
    if (Number.isSafeInteger(selectedCompanyId) && selectedCompanyId > 0 && selectedCompanyId !== Number(user.companyId)) {
      // switchCompany fetches the company through the API, which validates tenant access.
      company = await dispatch('switchCompany', { companyId: selectedCompanyId, permissions, user })
    }
    if (!company) {
      company = await dispatch('switchCompany', {
        companyId: user.companyId,
        permissions,
        user,
        persistUser: false,
      })
    }
    if (!company) {
      return false
    }
    commit('initialized', true)

    return true
  },

  switchCompany: async ({ dispatch }, { companyId, permissions = null, user = null, persistUser = true } = {}) => {
    if (!companyId) {
      return false
    }

    const activeUser = user || store.get('authStore@currentUser')
    const activePermissions = permissions || store.get('authStore@userPermissions')

    if (!activeUser) {
      return false
    }

    const company = await store.dispatch('companyStore/getById', companyId)
    if (!company?.data) {
      return false
    }

    const companyData = company.data

    await store.dispatch('crmOpportunityStore/reset')
    store.commit('authStore/setCompany', companyData)

    if (persistUser) {
      store.commit('authStore/updateUser', {
        ...activeUser,
        companyId: companyData.id,
      })
    }

    dispatch('setupMenu', { permissions: activePermissions, company: companyData, user: activeUser })
    dispatch('validateCompany', companyData)
    await dispatch('loadAccessControl', companyData)
    dispatch('notificationStore/get', {}, { root: true })

    return companyData
  },

  validateCompany: ({ state }, company) => {
    if (company.addresses?.length === 0) {
      return store.dispatch('app/showErrorCompany', 'Nenhum endereço da empresa encontrado')
    }

    if (!company.stateRegistration) {
      return store.dispatch('app/showErrorCompany', 'Inscrição estadual da empresa não cadastrada')
    }

    // if (!company.logoSmall)
    //   return store.dispatch('app/showErrorCompany', 'Logo da empresa não cadastrada')

    if (company.daysRemaining === 0) {
      // não bloquear por enquanto
      // state.dialogBlockByPayment = true
    }

    return true
  },

  showErrorCompany: ({ state }, text = '') => {
    state.dialogErrorCompany = true
    state.errorCompanyText = text
  },

  loadAccessControl: async ({ state }, company) => {
    if (!company?.id) {
      state.accessControl = null
      state.payment.dialog = false
      state.dialogBlockByPayment = false
      return null
    }

    try {
      const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/company-access-control/current`, {
        params: {
          companyId: company.id,
        },
      })

      state.accessControl = data || null
      state.payment.dialog = false
      state.dialogBlockByPayment = !data?.accessAllowed
      return data
    } catch (_error) {
      state.accessControl = null
      state.payment.dialog = false
      state.dialogBlockByPayment = false
      return null
    }
  },

  isAccessBlocked: (_, accessControl) => {
    if (!accessControl) {
      return false
    }
    return accessControl.accessAllowed === false || ['BLOCKED', 'MANUAL_BLOCKED'].includes(String(accessControl.status || '').toUpperCase())
  },

  setupMenu: ({ state, dispatch }, { permissions, company, user }) => {
    state.items = []

    dispatch('addMenuItem', {
      menuItem: { title: 'Painel', icon: 'mdi-view-dashboard', to: '/' },
    })

    if (user?.isGlobal) {
      dispatch('addMenuItem', {
        menuItem: {
          title: 'Assistente Inteligente',
          icon: 'mdi-robot-outline',
          to: '/assistente-inteligente',
        },
      })
    }

    if (allow(PERMISSIONS.USER_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Cadastros', icon: 'mdi-content-copy' },
        submenuItem: {
          title: 'Usuários',
          sub: true,
          icon: 'mdi-account-group',
          to: '/usuarios',
        },
      })
    }

    if (allow(PERMISSIONS.CUSTOMER_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Cadastros', icon: 'mdi-content-copy' },
        submenuItem: {
          title: 'Clientes',
          sub: true,
          icon: 'mdi-account-star-outline',
          to: '/clientes',
        },
      })
    }

    if (allow(PERMISSIONS.PROVIDER_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Cadastros', icon: 'mdi-content-copy' },
        submenuItem: {
          title: 'Fornecedores',
          sub: true,
          icon: 'mdi-account-tie-outline',
          to: '/fornecedores',
        },
      })
    }

    if (allow(PERMISSIONS.PRODUCT_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Cadastros', icon: 'mdi-content-copy' },
        submenuItem: {
          title: 'Produtos',
          sub: true,
          icon: 'mdi-package-variant',
          to: '/produtos',
        },
      })
    }

    if (allow(PERMISSIONS.SERVICE_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Cadastros', icon: 'mdi-content-copy' },
        submenuItem: {
          title: 'Serviços',
          sub: true,
          icon: 'mdi-tools',
          to: '/servicos',
        },
      })
    }

    if (allow(PERMISSIONS.CONTRACT_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Cadastros', icon: 'mdi-content-copy' },
        submenuItem: {
          title: 'Contratos',
          sub: true,
          icon: 'mdi-file-sign',
          to: '/contratos',
        },
      })
    }

    if (allow(PERMISSIONS.CRM_OPPORTUNITY_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Vendas', icon: 'mdi-cart' },
        submenuItem: { title: 'Funil de vendas', sub: true, icon: 'mdi-filter-variant', to: '/crm/oportunidades' },
      })
    }

    if (allow(PERMISSIONS.REQUEST_VIEW, permissions)) {
      const nomenclatura = getNomenclatura(NOMENCLATURA_PEDIDO, company.businessSegment)
      dispatch('addMenuItem', {
        menuItem: { title: 'Vendas', icon: 'mdi-cart' },
        submenuItem: {
          title: nomenclatura.capital() || 'Pedido',
          sub: true,
          icon: nomenclatura.icon || 'mdi-cart-arrow-up',
          to: nomenclatura.route,
        },
      })
    }

    if (allow(PERMISSIONS.REQUEST_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Vendas', icon: 'mdi-cart' },
        submenuItem: {
          title: 'Pagamentos',
          sub: true,
          icon: 'mdi-bank-transfer',
          to: '/pagamentos',
        },
      })
    }

    if (allow(PERMISSIONS.PURCHASE_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: {
          title: 'Compras',
          icon: 'mdi-cart-arrow-down',
          to: '/compras',
        },
      })
    }

    if (allow(PERMISSIONS.FISCAL_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: {
          title: 'Documentos Fiscais',
          icon: 'mdi-file-document',
          to: '/fiscal',
        },
      })
    }

    if (allow(PERMISSIONS.REVENUE_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Financeiro', icon: 'mdi-cash' },
        submenuItem: {
          title: 'Receitas',
          sub: true,
          icon: 'mdi-cash-plus',
          to: '/receitas',
        },
      })
    }

    if (allow(PERMISSIONS.EXPENSE_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Financeiro', icon: 'mdi-cash' },
        submenuItem: {
          title: 'Despesas',
          sub: true,
          icon: 'mdi-cash-minus',
          to: '/despesas',
        },
      })
    }

    if (allow(PERMISSIONS.BANK_RECONCILIATION_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Financeiro', icon: 'mdi-cash' },
        submenuItem: { title: 'Conciliação bancária', sub: true, icon: 'mdi-bank-check', to: '/conciliacao-bancaria' },
      })
    }

    if (allow(PERMISSIONS.ACCOUNT_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Financeiro', icon: 'mdi-cash' },
        submenuItem: {
          title: 'Contas',
          sub: true,
          icon: 'mdi-bank',
          to: '/contas',
        },
      })
    }

    dispatch('addMenuItem', {
      menuItem: { title: 'Ferramentas', icon: 'mdi-wrench' },
      submenuItem: {
        title: 'Agenda',
        sub: true,
        icon: 'mdi-calendar',
        to: '/agenda',
      },
    })

    if (allow(PERMISSIONS.GOAL_VIEW, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Ferramentas', icon: 'mdi-wrench' },
        submenuItem: {
          title: 'Metas',
          sub: true,
          icon: 'mdi-bullseye-arrow',
          to: '/metas',
        },
      })
    }

    dispatch('addMenuItem', {
      menuItem: { title: 'Ferramentas', icon: 'mdi-wrench' },
      submenuItem: {
        title: 'Tarefas',
        sub: true,
        icon: 'mdi-calendar-check',
        to: '/tarefas',
      },
    })

    if (allow(PERMISSIONS.REPBASIC, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Relatórios', icon: 'mdi-file' },
        submenuItem: {
          title: 'Cadastros',
          sub: true,
          icon: 'mdi-content-copy',
          to: '/relatorios/cadastros',
        },
      })
    }

    if (allow(PERMISSIONS.REPREQUEST, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Relatórios', icon: 'mdi-file' },
        submenuItem: {
          title: 'Pedidos',
          sub: true,
          icon: 'mdi-cart-arrow-up',
          to: '/relatorios/pedidos',
        },
      })
    }

    if (allow(PERMISSIONS.REPPURCHASE, permissions)) {
      // dispatch('addMenuItem', {
      //   menuItem: { title: "Relatórios", icon: "mdi-file" },
      //   submenuItem: {
      //     title: "Compras",
      //     sub: true,
      //     icon: "mdi-cart-arrow-down",
      //     to: "/relatorios/compras",
      //   }
      // })
    }

    if (allow(PERMISSIONS.REPFINANCIAL, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Relatórios', icon: 'mdi-file' },
        submenuItem: {
          title: 'Financeiro',
          sub: true,
          icon: 'mdi-cash',
          to: '/relatorios/financeiro',
        },
      })
    }

    if (allow(PERMISSIONS.REPMANAGE, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Relatórios', icon: 'mdi-file' },
        submenuItem: {
          title: 'Gerencial',
          sub: true,
          icon: 'mdi-office-building-outline',
          to: '/relatorios/gerencial',
        },
      })
    }
    if (allow(PERMISSIONS.CONFIG, permissions)) {
      dispatch('addMenuItem', {
        menuItem: { title: 'Configurações', icon: 'mdi-cog', to: '/configuracoes' },
      })
    }

    dispatch('addMenuItem', {
      menuItem: { title: 'Adicionar', icon: 'mdi-plus', action: 'add' },
    })
  },

  addMenuItem: ({ state }, { menuItem, submenuItem }) => {
    const menuItemAux = state.items.find(i => i.title === menuItem.title)

    if (submenuItem) {
      if (menuItemAux) {
        menuItemAux.items.push({ ...submenuItem })
      } else {
        menuItem.items = [submenuItem]
        state.items.push(menuItem)
      }
    } else {
      if (!menuItemAux) {
        state.items.push(menuItem)
      }
    }
  },
}

const getters = {}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
